import styled, { css } from 'styled-components';
import { COLORS } from 'assets/styled/colors';
import { borderRadiusSm, marginMd, stickyTopOffset } from 'assets/styled';
import { respondTo } from 'assets/styled/breakpoints';

interface StyledProps {
  isPaginationTR: boolean;
  isPaginationPresent: boolean;
  $withoutPagination: boolean;
  isClickable: boolean;
  flexValue: number;
  extraActionsFlexValue: number;
  $bigCell: true;
  labelsRow: boolean;
  $withoutMarginTop?: boolean;
  isHovered?: boolean;
  selectField?: boolean;
  $allowTextSelection?: boolean;
  $isUppercase?: boolean;
  $stickyOffset?: number;
}

export const TableResponsive = styled.div`
  -webkit-overflow-scrolling: touch;
  position: relative;

  ${respondTo.horizontalTablet`
    overflow-x: auto;
    max-width: 100%;
  `}
`;

/*
 * Sticky header layout. A sticky thead cannot live inside TableResponsive:
 * below horizontalTablet it sets overflow-x, which computes overflow-y to auto
 * and makes it the scrollport the thead would stick to. So the header is
 * rendered as its own table above TableResponsive, and its horizontal scroll
 * is mirrored from the body in Table.tsx. Column widths come from flex values
 * and min-widths rather than cell content, so both tables line up.
 *
 * The wrapper bounds how far the header follows: it stays stuck only while the
 * table itself is on screen. It also isolates the header's z-index, which only
 * has to beat the table's own rows - without it the header would cover
 * anything on the page that opens over the table (e.g. BOM "Manage table").
 */
export const StickyTableWrapper = styled.div`
  position: relative;
  isolation: isolate;
`;

export const StickyHeader = styled.div`
  position: sticky;
  top: calc(
    ${stickyTopOffset} + ${(props: StyledProps) => props.$stickyOffset ?? 0}px
  );
  /* Above row content (Icon uses z-index 1), below the fixed bars */
  z-index: 2;
  /*
   * Carries the table's top margin: inside the sticky box it would leave a
   * see-through strip above the header once stuck.
   */
  margin-top: ${marginMd};

  ${(props: StyledProps) =>
    (props.$withoutMarginTop || props.$withoutPagination) &&
    css`
      margin-top: 0;
    `}
`;

// Scrolled programmatically only, so a swipe on the header does not drift it
// out of line with the rows.
export const StickyHeaderScroller = styled.div`
  overflow: hidden;
`;

export const CustomTable = styled.table`
  margin-top: ${marginMd};
  border-collapse: collapse;
  max-width: 100%;
  width: 100%;

  ${(props: StyledProps) =>
    props.$withoutMarginTop &&
    css`
      margin-top: 0;
    `}

  ${(props: StyledProps) =>
    props.$withoutPagination &&
    css`
      margin-top: 0;
    `}

  th {
    background-color: ${COLORS.GREEN};
    color: ${COLORS.WHITE};
    font-weight: 300;
    display: flex;
    align-items: flex-start;

    label {
      color: ${COLORS.WHITE};
    }
  }
  th:first-child {
    border-top-left-radius: ${borderRadiusSm};
    flex: 0;
  }
  th:last-child {
    border-top-right-radius: ${borderRadiusSm};
    flex: 0;
  }

  th,
  td {
    min-width: 0;
    padding: 10rem 10rem;
    vertical-align: middle;
    text-align: left;
    font-weight: 500;
    transition: ease 200ms;
  }
  td {
    padding: 20rem 10rem;
    background-color: ${COLORS.WHITE};
  }

  ${respondTo.horizontalTablet`
    th, td label {
      line-height: 1.3;
      font-size: 18rem;
    }

    th,
    td {
      min-width: 100rem;
      padding: 20rem 5rem;
    }
    th {
      padding: 10rem 5rem;
    }
    td:first-child,
    th:first-child {
      min-width: 0;
    }
    td:last-child,
    th:last-child {
      min-width: 0;
    }
  `}

  td:first-child {
    flex: 0;
    border: none;
    width: 30rem;
  }
  td:last-child {
    flex: 0;
    border: none;
    width: 30rem;
  }

  tr:not(:last-child) {
    td {
      display: flex;
      align-items: center;
    }
  }

  tr:last-child {
    border-color: transparent;
    td {
      flex: 1;
      padding: 10rem;
    }
    td:first-child {
      border-bottom-left-radius: ${borderRadiusSm};
      flex: 0;
    }
    td:last-child {
      border-bottom-right-radius: ${borderRadiusSm};
      flex: 0;
    }
  }
`;

export const TableRow = styled.tr`
  td {
    background-color: ${(props: StyledProps) =>
      props.isHovered ? COLORS.GRAY_300 : COLORS.WHITE};
  }
`;

export const TD = styled.td`
  ${(props: StyledProps) =>
    props.flexValue &&
    css`
      flex: ${props.flexValue};
    `};

  border-bottom: 1px solid ${COLORS.GRAY_300};

  ${(props: StyledProps) =>
    props.labelsRow &&
    css`
      border: none;
    `};
`;

export const PaginationTR = styled.tr`
  height: 84rem;
  pointer-events: none;
  user-select: none;
  ${(props: StyledProps) =>
    !props.isPaginationPresent &&
    css`
      height: 50rem;
    `}
`;

export const TdLabel = styled.label`
  vertical-align: middle;
  text-align: left;
  font-weight: 500;
  text-transform: ${(props: StyledProps) =>
    props.$isUppercase ? 'uppercase' : 'none'};
  user-select: ${(props: StyledProps) =>
    props.$allowTextSelection ? 'text' : 'none'};
  position: relative;
  word-break: normal;
  color: ${COLORS.BLACK};
`;

export const ThLabel = styled.label`
  vertical-align: middle;
  text-align: left;
  font-weight: 500;
  user-select: none;
  position: relative;
  word-break: normal;

  ${(props: StyledProps) =>
    props.isClickable &&
    css`
      cursor: pointer;
    `}
`;

export const ThWrapper = styled.div`
  display: flex;
  align-items: center;
`;

export const ThIcon = styled.div`
  display: flex;
  align-items: center;
  gap: 10rem;
  margin-top: 15rem;

  ${respondTo.horizontalTablet`
    margin-top: 10rem;
  `}
`;

export const Th = styled.th`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  ${(props: StyledProps) =>
    props.flexValue &&
    css`
      flex: ${props.flexValue};
    `}

  ${(props: StyledProps) =>
    props.$bigCell &&
    css`
      justify-content: space-between;
    `}
`;

export const ExtraActionsHeader = styled.th`
  ${(props: StyledProps) =>
    props.extraActionsFlexValue &&
    css`
      flex: ${props.extraActionsFlexValue};

      ${respondTo.horizontalTablet`
        min-width: 100rem !important;
      `}
    `}
`;

export const ExtraActionsHeaderInvisibleContentWrapper = styled.div`
  opacity: 0;
  user-select: none;
  pointer-events: none;
  display: flex;
`;

export const ExtraActionsTableData = styled.td`
  display: flex;
  justify-content: space-evenly;
  gap: 10rem;
  ${(props: StyledProps) =>
    props.extraActionsFlexValue &&
    css`
      flex: ${props.extraActionsFlexValue};

      ${respondTo.horizontalTablet`
        min-width: 100rem !important;
      `}
    `}

  border-bottom: 1px solid ${COLORS.GRAY_300};

  ${(props: StyledProps) =>
    props.labelsRow &&
    css`
      border: none;
    `};
`;

export const CardList = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${COLORS.WHITE};
  padding: 20rem;
  border-radius: 10rem;
  width: 100%;
  max-width: 100%;
`;

export const CardWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0 4%;
`;
export const CardHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5rem;
`;
export const CardHeaderLabelWrapper = styled.div`
  min-height: 30rem;
  display: flex;
  align-items: center;

  ${(props: StyledProps) =>
    props.selectField &&
    css`
      height: 43rem;
    `};
`;
export const CardHeaderLabel = styled.label`
  font-weight: 300;
  font-size: 16rem;
  color: ${COLORS.BLACK};
  line-height: 1;
`;
export const Card = styled.div`
  max-width: 48%;
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  border-bottom: 1px solid ${COLORS.GRAY_300};
  padding-bottom: 20rem;
  margin-bottom: 20rem;
  justify-content: space-between;
  position: relative;

  &:nth-child(1n) {
    &::before {
      content: '';
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      right: -4%;
      background-color: ${COLORS.GRAY_300};
      width: 1px;
      height: 80%;

      ${respondTo.bigPhone`
        display: none;
      `}
    }
  }

  &:nth-child(2n) {
    &::before {
      display: none;
    }
  }

  ${respondTo.bigPhone`
    max-width: 100%;
  `}
`;
export const CardLeftSide = styled.div`
  max-width: 50%;
  width: 100%;
`;
export const CardRightSide = styled.div`
  max-width: 50%;
  width: 100%;
`;
export const CardValueWrapper = styled.div`
  min-height: 30rem;
  display: flex;
  align-items: center;
`;

export const CardValueHolder = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5rem;
`;

export const CardValue = styled.label`
  font-weight: 300;
  text-transform: ${(props: StyledProps) =>
    props.$isUppercase ? 'uppercase' : 'none'};
  line-height: 1;
  font-size: 16rem;
  color: ${COLORS.BLACK};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  cursor: pointer;
  color: ${COLORS.GREEN};
`;
export const ExtraActionsCard = styled.div`
  width: 100%;
  max-width: 100%;
  margin-top: 20rem;
  display: flex;
  gap: 10rem;
  justify-content: center;
`;

export const PaginationWrapper = styled.div`
  max-width: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
`;

export const ExtraActionsWrapper = styled.div`
  display: flex;
  gap: 10rem;
  justify-content: center;
`;

export const LoadMoreWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 40rem;
`;
